import {
  Badge,
  type CSS,
  FilterButton,
  Flex,
  MultiSelect,
  Text,
  TextField,
  Tooltip,
  styled,
} from '@kandji-inc/nectar-ui';
import { i18n } from 'i18n';
import * as React from 'react';

import { DeviceFamilyFilterWrapper } from 'src/components/platforms/DeviceFamilyFilter/DeviceFamilyFilter';
import {
  type DeviceFilter,
  DeviceSelectValues,
} from 'src/components/platforms/DeviceFamilyFilter/DeviceFamilyFilter.types';
import {
  type DeviceFamilyTriggerProps,
  DeviceFilterTriggers,
} from 'src/components/platforms/DeviceFamilyFilter/DeviceFilterTriggers';
import { useBlueprints } from 'src/features/visibility/prism/hooks';
import { useFocusInput } from 'src/hooks/useFocusInput';
import { useGetEnabledPlatforms } from 'src/hooks/useGetEnabledPlatforms';
import { useCrossPlatformGlobalFilter } from './CrossPlatformGlobalFilterContext';

interface CrossPlatformGlobalFilterBarProps {
  fallback: React.ReactNode;
}

export const CrossPlatformGlobalFilterBar = ({
  fallback,
}: CrossPlatformGlobalFilterBarProps) => {
  const { crossPlatformEnabled } = useGetEnabledPlatforms();
  const {
    deviceFamilyFilters,
    setDeviceFamilyFilters,
    resetDeviceFamilyFilters,
    blueprintFilters,
    setBlueprintFilters,
    resetBlueprintFilters,
  } = useCrossPlatformGlobalFilter();

  const deviceFamilyFilterArray = React.useMemo(() => {
    return Object.values(deviceFamilyFilters);
  }, [deviceFamilyFilters]);

  const handleDeviceFamilyFiltersChange = (filters: DeviceFilter[]) => {
    filters.forEach((filter) => {
      if (!filter.id) {
        return;
      }

      const nextDeviceFamilyFilter = { ...filter };
      const isRemoveParentDeviceFamilyFilter =
        filter.value === DeviceSelectValues.UNCHECKED;

      if (isRemoveParentDeviceFamilyFilter) {
        nextDeviceFamilyFilter.children =
          filter.children?.map((child) => ({
            ...child,
            value: DeviceSelectValues.UNCHECKED,
          })) ?? [];
      }

      if (Array.isArray(filter.children)) {
        nextDeviceFamilyFilter.children = filter.children.map((child) => ({
          ...child,
        }));
      }

      setDeviceFamilyFilters(filter.id.toLowerCase(), nextDeviceFamilyFilter);
    });
  };

  const Trigger = React.useMemo(
    () =>
      TooltipTrigger({
        content: (
          <StyledTooltipContent
            flow="column"
            gap="md"
            data-testid="device-family-filter-tooltip"
          >
            {Object.values(deviceFamilyFilters)
              .filter((filter) => filter.value !== DeviceSelectValues.UNCHECKED)
              .map((filter) => (
                <Flex key={filter.id} flow="column" gap="xs">
                  <Text
                    size="2"
                    css={{
                      fontWeight: '$medium',
                    }}
                  >
                    {i18n.t('{family} Devices', { family: filter.label })}
                  </Text>
                  {filter.children?.length ? (
                    <ul>
                      {filter.children
                        .filter(
                          (child) => child.value === DeviceSelectValues.CHECKED,
                        )
                        .map((child) => (
                          <li key={child.id}>
                            <Text>{child.label}</Text>
                          </li>
                        ))}
                    </ul>
                  ) : null}
                </Flex>
              ))}
          </StyledTooltipContent>
        ),
        side: 'bottom',
        css: {
          zIndex: 999999,
        },
        interactive: true,
      }),
    [deviceFamilyFilters],
  );

  return (
    <Flex gap="sm">
      <DeviceFamilyFilterWrapper
        filters={deviceFamilyFilterArray}
        fallback={fallback}
        onChange={handleDeviceFamilyFiltersChange}
        onClear={resetDeviceFamilyFilters}
        trigger={Trigger}
        menuCss={{
          width: '160px',
          '[role="option"][data-item-level="1"]': {
            paddingLeft: '15px',
          },
          '[role="option"][data-item-level="2"]': {
            paddingLeft: '$6',
          },
        }}
        menuFooterCss={{
          paddingTop: '$3',
          paddingBottom: '$3',
        }}
      />

      {crossPlatformEnabled && (
        <BlueprintFilterWrapper
          filters={blueprintFilters}
          onChange={setBlueprintFilters}
          onClear={resetBlueprintFilters}
        />
      )}
    </Flex>
  );
};

interface BlueprintFilterProps {
  filters: string[];
  onChange: (value: string[]) => void;
  onClear: () => void;
}

const BlueprintFilterWrapper = ({
  filters = [],
  onChange,
  onClear,
}: BlueprintFilterProps) => {
  const { data: blueprints = [] } = useBlueprints();
  const [blueprintSearchInput, setBlueprintSearchInput] = React.useState('');
  const [blueprintOptions, allBlueprintOptions] = React.useMemo(() => {
    const options = blueprints.map((blueprint) => ({
      label: blueprint.name,
      value: blueprint.id,
    }));

    if (blueprintSearchInput) {
      return [
        options.filter((option) =>
          option.label
            .toLowerCase()
            .includes(blueprintSearchInput.toLowerCase()),
        ),
        options,
      ];
    }

    return [options, options];
  }, [blueprints, blueprintSearchInput]);

  const [align, setAlign] = React.useState<'left' | 'right'>('right');
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const blueprintSearchRef = useFocusInput([isDropdownOpen]);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  const hasFilters = Boolean(filters.length);

  const onOpenChange = (open: boolean) => {
    if (open) {
      const trigger = triggerRef.current;
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        setAlign(window.innerWidth - rect.right < 320 ? 'right' : 'left');
      }
    }
  };

  const reAlignMultiSelectProps = () => ({
    multiSelect: {
      onOpenChange,
      componentCss: {
        menu: {
          [align]: '0',
        },
      },
    },
    filterButton: {
      ref: (r) => {
        triggerRef.current = r;
      },
    },
  });

  const getFilterBadge = (selectedFilterOptions, max: number) => {
    if (
      selectedFilterOptions.length > 1 &&
      selectedFilterOptions.length !== max
    ) {
      const count = selectedFilterOptions.length - 1;
      return (
        <Tooltip
          content={
            <StyledTooltipContent
              flow="column"
              gap="xs"
              data-testid="blueprint-filter-tooltip"
            >
              <Text
                size="2"
                css={{
                  fontWeight: '$medium',
                }}
              >
                {i18n.t('Blueprints')}
              </Text>
              <ul>
                {selectedFilterOptions.map((blueprint) => (
                  <li key={blueprint.value}>
                    <Text>{blueprint.label}</Text>
                  </li>
                ))}
              </ul>
            </StyledTooltipContent>
          }
          side="bottom"
          css={{
            zIndex: 999999,
            maxHeight: '400px',
            overflow: 'auto',
            minWidth: '300px',
          }}
          interactive
        >
          <Badge
            compact
            css={{
              color: '$neutral0',
              backgroundColor: '$blue50',
              marginLeft: '$1',
            }}
          >
            +{count}
          </Badge>
        </Tooltip>
      );
    }

    return null;
  };

  const selectedFilterOptions = filters.map((filter) =>
    allBlueprintOptions.find(({ value }) => value === filter),
  );
  const isAllBlueprintsSelected = filters.length === blueprints.length;
  const lastSelectedBlueprintOption = allBlueprintOptions.find(
    ({ value }) => value === filters[0],
  );

  const handleOnChange = (selectedBlueprintValues: string[]) => {
    onChange(selectedBlueprintValues);
  };

  return (
    <MultiSelect
      multi
      options={blueprintOptions}
      selectAll={{
        selectAllLabel: i18n.t('Select all'),
        onSelectAll: () =>
          handleOnChange(blueprintOptions.map((blueprint) => blueprint.value)),
        onDeselectAll: onClear,
      }}
      value={filters}
      onChange={handleOnChange}
      footer={{
        showClear: true,
        clearLabel: i18n.t('Clear'),
        clearDisabled: !hasFilters,
        handleClear: onClear,
      }}
      customHeader={
        <TextField
          ref={blueprintSearchRef}
          compact
          p2
          placeholder={i18n.t('Search Blueprints')}
          icon="magnifying-glass"
          value={blueprintSearchInput}
          onChange={(e) => setBlueprintSearchInput(e.target.value)}
          onClear={() => setBlueprintSearchInput('')}
          showClearButton={Boolean(blueprintSearchInput)}
        />
      }
      {...reAlignMultiSelectProps().multiSelect}
      onOpenChange={(isOpen) => {
        setIsDropdownOpen(isOpen);
        reAlignMultiSelectProps().multiSelect.onOpenChange(isOpen);
      }}
      componentCss={{
        menu: {
          zIndex: '$1',
        },
      }}
    >
      <FilterButton
        filtersSelected={hasFilters}
        showRemove={false}
        aria-label="global-blueprints-filter-button"
        data-testid="blueprint-filter-button"
      >
        <Flex
          {...reAlignMultiSelectProps().filterButton}
          flow="row"
          alignItems="center"
          gap="xs"
        >
          <Text>
            {i18n.t('Blueprint{withColon}', {
              withColon: hasFilters ? ': ' : '',
            })}
          </Text>
          {hasFilters && (
            <Text
              css={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontWeight: '$medium',
              }}
            >
              {isAllBlueprintsSelected
                ? i18n.t('All')
                : lastSelectedBlueprintOption?.label}
            </Text>
          )}
          {getFilterBadge(selectedFilterOptions, blueprintOptions.length)}
        </Flex>
      </FilterButton>
    </MultiSelect>
  );
};

const TooltipTrigger = (tooltip: {
  content: React.ReactNode;
  css?: CSS;
  side?: 'top' | 'bottom';
  interactive?: boolean;
}) =>
  React.forwardRef<HTMLButtonElement, Omit<DeviceFamilyTriggerProps, 'ref'>>(
    (props, ref) => (
      <DeviceFilterTriggers.Default
        {...props}
        counterTooltip={tooltip}
        ref={ref}
        data-testid="device-family-filter-trigger"
      />
    ),
  );

const StyledTooltipContent = styled(Flex, {
  minWidth: '200px',
  ul: {
    margin: 0,
    paddingLeft: '$4',
  },
  li: {
    margin: 0,
    padding: 0,
  },
});
